/* Automatizaciones: «cuando pasa esto, hacé esto», armado con bloques.
 *
 * Igna, 03/10/2026: «un área para automatizar flujos, en estilo programación
 * por bloques, que todos puedan verlo». El hub ARMA y MUESTRA; el que CORRE
 * es el bot (FlotaBot, con service_role). Lo que los une es la forma de
 * `disparador` y `pasos`, escrita en el encabezado de
 * `supabase/migrations/20261003040000_automatizaciones.sql`. Ese comentario
 * es el contrato: si algo de acá cambia la forma del jsonb, cambia allá y en
 * el bot el mismo día.
 *
 * ── POR QUÉ TODO ESTO ES PURO Y VIVE APARTE ─────────────────────────────
 *
 * Lo que puede romper una automatización no es la pantalla: es un jsonb mal
 * armado que el bot no entiende, una variable que no existe y sale tal cual
 * («{{precio}}») en el WhatsApp de alguien, o un rango «B2:D» que el bot no
 * sabe leer. Acá son funciones puras —entra el formulario, sale el jsonb, y
 * al revés— y se prueban sin dibujar nada. La pantalla sólo decide cuándo
 * llamarlas.
 *
 * ── LO QUE NO SE PUEDE, Y SE CORTA ACÁ ANTES QUE EN EL BOT ──────────────
 *
 * · Escribir en una planilla ajena: `escribir_celda` sólo acepta planillas
 *   del dueño. El bot lo vuelve a controlar al correr (no confía en el hub),
 *   pero avisar al guardar es mejor que una corrida en rojo un viernes.
 * · Mandar a alguien de afuera: `a` es "yo" o mails de `gente_para_compartir()`.
 * · Mandar una planilla COMPARTIDA conmigo a otro (`mandar_planilla`, 05/10):
 *   una mía va a quien yo elija del hub; una que me compartieron, sólo a mí.
 *   Si no, la automatización sería un reenvío semanal de datos ajenos a gente
 *   que la dueña nunca eligió.
 * · Código: los bloques son un menú cerrado. Nada de lo que se escribe acá se
 *   ejecuta; las variables son reemplazos de texto.
 */

import { colALetra, parseRef } from "./planilla";

/* ── El catálogo de bloques ─────────────────────────────────────────────── */

/* 0 = domingo … 6 = sábado, como `Date.getDay()` y como dice el contrato. */
export const DIAS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const DIAS_LARGOS = ["domingos", "lunes", "martes", "miércoles", "jueves", "viernes", "sábados"];

export const DISPARADORES = [
  { tipo: "horario", label: "A una hora", sub: "Ciertos días, a cierta hora" },
  { tipo: "mail", label: "Llega un mail", sub: "A la etiqueta Automatizaciones" },
  { tipo: "planilla_cambia", label: "Cambia una planilla", sub: "Un rango de una planilla tuya" },
];

/* «Tomá…» deja un dato en una variable; «Hacé…» actúa. Se dibujan en dos
   grupos pero viajan en UNA lista ordenada (el contrato): primero los que
   toman, después los que hacen. Así cualquier «Hacé» puede usar lo que tomó
   cualquier «Tomá». */
export const PASOS = {
  leer_mail_ia: { grupo: "toma", label: "Leer el mail con IA", guarda: true },
  leer_celda: { grupo: "toma", label: "Leer una celda", guarda: true },
  escribir_celda: { grupo: "hace", label: "Escribir en una celda" },
  whatsapp: { grupo: "hace", label: "Mandar un WhatsApp" },
  mail: { grupo: "hace", label: "Mandar un mail" },
  mandar_planilla: { grupo: "hace", label: "Mandar una planilla" },
};

/* Por dónde sale `mandar_planilla` (FiboSheets #9, 05/10): por mail, el Excel
   entero con los valores calculados; por WhatsApp, un resumen de las primeras
   filas. Uno, el otro o los dos. */
export const CANALES_PLANILLA = [
  { canal: "mail", label: "Por mail, el Excel" },
  { canal: "whatsapp", label: "Por WhatsApp, un resumen" },
];
export const grupoDe = (tipo) => (PASOS[tipo] ? PASOS[tipo].grupo : null);

/* Las variables que pone el disparador, según el contrato. `{{rango}}` en
   `planilla_cambia` la sumamos con el aviso de FiboSheets (Igna, 03/10/2026):
   el texto «Cambió {{planilla}} ({{rango}})» la necesita, y el bot la tiene
   que poner igual que `{{planilla}}`. */
export const VARIABLES_DISPARADOR = {
  horario: ["fecha"],
  mail: ["asunto", "remitente", "fecha"],
  planilla_cambia: ["cambios", "planilla", "rango"],
};

/* Qué es cada variable, para el `title` del chip. */
export const AYUDA_VARIABLE = {
  fecha: "El día y la hora en que corre",
  asunto: "El asunto del mail",
  remitente: "Quién mandó el mail",
  cambios: "Lo que cambió, una celda por renglón (B3: 100 → 120)",
  planilla: "El nombre de la planilla",
  rango: "El rango que se mira (B2:D10)",
};

/* ── Celdas y rangos ─────────────────────────────────────────────────────
 *
 * Se apoyan en `parseRef` del motor de FiboSheets: lo que acá es una celda
 * válida es exactamente lo que la planilla entiende como celda. Sin `$`: en una
 * automatización no hay nada que copiar ni rellenar, y un «$B$3» guardado
 * sería una forma más que el bot tendría que saber leer. */

const CELDA_RE = /^[A-Za-z]{1,3}\d{1,7}$/;

export function celdaValida(txt) {
  const s = String(txt ?? "").trim();
  if (!CELDA_RE.test(s)) return false;
  const p = parseRef(s);
  return !!p && p.fila >= 0 && p.col >= 0;
}

/* «b3 » → «B3». Lo inválido vuelve tal cual (recortado), para que el error
   se vea con lo que escribió la persona. */
export function normalizarCelda(txt) {
  const s = String(txt ?? "").trim();
  return celdaValida(s) ? s.toUpperCase() : s;
}

/* «B2:D10» (o una sola celda) → `{ r0, c0, r1, c1 }` con las esquinas en
   orden. `null` si no es un rango. */
export function parseRango(txt) {
  const s = String(txt ?? "").trim();
  if (!s) return null;
  const partes = s.split(":");
  if (partes.length > 2) return null;
  if (!partes.every(celdaValida)) return null;
  const a = parseRef(partes[0]);
  const b = parseRef(partes[1] || partes[0]);
  return {
    r0: Math.min(a.fila, b.fila), r1: Math.max(a.fila, b.fila),
    c0: Math.min(a.col, b.col), c1: Math.max(a.col, b.col),
  };
}

export const rangoValido = (txt) => parseRango(txt) !== null;

/* Un rango como lo guarda el jsonb: mayúsculas y la esquina de arriba a la
   izquierda primero («d10:b2» → «B2:D10»; «B3:B3» → «B3»). Lo inválido vuelve
   recortado y nada más. */
export function normalizarRango(txt) {
  const rg = parseRango(txt);
  if (!rg) return String(txt ?? "").trim();
  return textoDeRango(rg);
}

export function textoDeRango(rg) {
  const a = `${colALetra(rg.c0)}${rg.r0 + 1}`;
  const b = `${colALetra(rg.c1)}${rg.r1 + 1}`;
  return a === b ? a : `${a}:${b}`;
}

/* ── Variables ───────────────────────────────────────────────────────── */

const NOMBRE_VAR_RE = /^[A-Za-z_áéíóúñÁÉÍÓÚÑ][A-Za-z0-9_áéíóúñÁÉÍÓÚÑ]*$/;
export const nombreVariableValido = (n) => NOMBRE_VAR_RE.test(String(n ?? "").trim());

/* Los `{{nombre}}` que usa un texto, sin repetir y en el orden en que
   aparecen. Los espacios adentro de las llaves no cuentan («{{ precio }}»). */
export function variablesUsadas(texto) {
  const salida = [];
  const re = /\{\{\s*([^{}]*?)\s*\}\}/g;
  let m;
  while ((m = re.exec(String(texto ?? ""))) !== null) {
    if (!salida.includes(m[1])) salida.push(m[1]);
  }
  return salida;
}

/* Las variables que existen ANTES del paso `indice` (las del disparador más
   el `guardar_como` de los pasos anteriores). Con `indice` = cantidad de
   pasos, son todas. Es lo que se ofrece como chip en cada bloque. */
export function variablesDisponibles(disparador, pasos, indice = (pasos || []).length) {
  const salida = [...(VARIABLES_DISPARADOR[disparador && disparador.tipo] || [])];
  (pasos || []).slice(0, indice).forEach((p) => {
    const n = String((p && p.guardar_como) || "").trim();
    if (p && PASOS[p.tipo] && PASOS[p.tipo].guarda && n && !salida.includes(n)) salida.push(n);
  });
  return salida;
}

/* Mete `{{nombre}}` donde está el cursor (o reemplaza lo seleccionado) y
   devuelve el texto nuevo y dónde queda el cursor. */
export function insertarVariable(texto, nombre, desde, hasta = desde) {
  const t = String(texto ?? "");
  const a = Number.isInteger(desde) ? Math.max(0, Math.min(desde, t.length)) : t.length;
  const b = Number.isInteger(hasta) ? Math.max(a, Math.min(hasta, t.length)) : a;
  const chip = `{{${nombre}}}`;
  return { texto: t.slice(0, a) + chip + t.slice(b), cursor: a + chip.length };
}

/* ── El formulario ↔ el jsonb ──────────────────────────────────────────
 *
 * El formulario guarda TODO lo que la persona tocó (si cambia de «A una hora»
 * a «Llega un mail» y vuelve, los días siguen ahí). El jsonb lleva sólo las
 * claves del contrato para ese tipo: una clave de más es una clave que el bot
 * podría empezar a leer por accidente. */

let semilla = 0;
const clave = () => `p${Date.now().toString(36)}${(semilla += 1)}`;

export function disparadorVacio(tipo = "horario") {
  return { tipo, dias: [1, 2, 3, 4, 5], hora: "09:00", remitente: "", asunto: "", planilla_id: "", hoja: null, rango: "" };
}

export function pasoVacio(tipo) {
  const base = { _k: clave(), tipo };
  switch (tipo) {
    case "leer_mail_ia": return { ...base, que: "", guardar_como: "" };
    case "leer_celda": return { ...base, planilla_id: "", hoja: null, celda: "", guardar_como: "" };
    case "escribir_celda": return { ...base, planilla_id: "", hoja: null, celda: "", valor: "" };
    case "whatsapp": return { ...base, a: ["yo"], texto: "" };
    case "mail": return { ...base, a: ["yo"], asunto: "", texto: "" };
    case "mandar_planilla": return { ...base, planilla_id: "", a: ["yo"], por: ["mail"] };
    default: return base;
  }
}

export function formVacio() {
  return { nombre: "", descripcion: "", disparador: disparadorVacio("horario"), pasos: [pasoVacio("whatsapp")] };
}

const txt = (v) => String(v ?? "").trim();
const hojaONull = (h) => (txt(h) ? txt(h) : null);
/* Los canales en el orden del catálogo, sin repetir ni inventar. */
const canales = (por) => CANALES_PLANILLA.map((c) => c.canal).filter((c) => (Array.isArray(por) ? por : []).includes(c));
const destinatarios = (a) => {
  const vistos = [];
  (Array.isArray(a) ? a : []).forEach((x) => {
    const m = txt(x).toLowerCase();
    if (m && !vistos.includes(m)) vistos.push(m);
  });
  return vistos;
};

export function disparadorAJson(d) {
  const x = d || {};
  if (x.tipo === "horario") {
    const dias = [...new Set((x.dias || []).map(Number).filter((n) => Number.isInteger(n) && n >= 0 && n <= 6))]
      .sort((a, b) => a - b);
    return { tipo: "horario", dias, hora: txt(x.hora) };
  }
  if (x.tipo === "mail") return { tipo: "mail", remitente: txt(x.remitente), asunto: txt(x.asunto) };
  if (x.tipo === "planilla_cambia") {
    return { tipo: "planilla_cambia", planilla_id: txt(x.planilla_id), hoja: hojaONull(x.hoja), rango: normalizarRango(x.rango) };
  }
  return { tipo: x.tipo || null };
}

export function pasoAJson(p) {
  const x = p || {};
  switch (x.tipo) {
    case "leer_mail_ia": return { tipo: x.tipo, que: txt(x.que), guardar_como: txt(x.guardar_como) };
    case "leer_celda":
      return { tipo: x.tipo, planilla_id: txt(x.planilla_id), hoja: hojaONull(x.hoja), celda: normalizarCelda(x.celda), guardar_como: txt(x.guardar_como) };
    case "escribir_celda":
      // El valor NO se recorta: un espacio adelante puede ser a propósito.
      return { tipo: x.tipo, planilla_id: txt(x.planilla_id), hoja: hojaONull(x.hoja), celda: normalizarCelda(x.celda), valor: String(x.valor ?? "") };
    case "whatsapp": return { tipo: x.tipo, a: destinatarios(x.a), texto: String(x.texto ?? "").trim() };
    case "mail": return { tipo: x.tipo, a: destinatarios(x.a), asunto: txt(x.asunto), texto: String(x.texto ?? "").trim() };
    case "mandar_planilla": return { tipo: x.tipo, planilla_id: txt(x.planilla_id), a: destinatarios(x.a), por: canales(x.por) };
    default: return { tipo: x.tipo };
  }
}

/* Primero los que toman, después los que hacen, cada grupo en su orden. */
export function ordenarPasos(pasos) {
  const lista = pasos || [];
  return [...lista.filter((p) => grupoDe(p.tipo) === "toma"), ...lista.filter((p) => grupoDe(p.tipo) !== "toma")];
}

/* Lo que va a la tabla `automatizaciones` (sin `activa` ni `dueno`: el
   primero lo decide quién llama —nueva = apagada— y el segundo la base). */
export function formAJson(form) {
  const f = form || {};
  return {
    nombre: txt(f.nombre),
    descripcion: txt(f.descripcion) || null,
    disparador: disparadorAJson(f.disparador),
    pasos: ordenarPasos(f.pasos).map(pasoAJson),
  };
}

/* Una fila de la base → el formulario. Lo que falte se completa con lo de un
   bloque vacío, así un jsonb viejo o a medias se abre igual. */
export function jsonAForm(fila) {
  const f = fila || {};
  const d = f.disparador || {};
  const base = disparadorVacio(d.tipo || "horario");
  const disparador = { ...base, ...d, hoja: d.hoja ?? null };
  if (!Array.isArray(disparador.dias)) disparador.dias = base.dias;
  const pasos = ordenarPasos((Array.isArray(f.pasos) ? f.pasos : []).map((p) => {
    const vacio = pasoVacio(p && p.tipo);
    return {
      ...vacio, ...p, _k: vacio._k,
      a: p && Array.isArray(p.a) ? p.a : vacio.a,
      ...(vacio.por ? { por: p && Array.isArray(p.por) ? p.por : vacio.por } : {}),
    };
  }));
  return { nombre: f.nombre || "", descripcion: f.descripcion || "", disparador, pasos };
}

/* ── El aviso de FiboSheets ────────────────────────────────────────────
 *
 * «Avisarme por WhatsApp si esto cambia» sobre un rango elegido. Nace PRENDIDA
 * —la única que nace así—: la persona lo pidió explícitamente sobre esas
 * celdas, y una campanita que hay que ir a prender a otra pantalla no avisa.
 * `hoja` va en null cuando el libro tiene una sola hoja: así sigue andando si
 * la renombra o agrega otra después (null = la primera). */
export const TEXTO_AVISO = "Cambió {{planilla}} ({{rango}}):\n{{cambios}}";

export function avisoDeRango({ planillaId, planillaNombre, hoja, rango }) {
  const r = normalizarRango(rango);
  return {
    nombre: `Aviso: ${txt(planillaNombre) || "planilla"} ${r}`,
    descripcion: null,
    activa: true,
    disparador: { tipo: "planilla_cambia", planilla_id: planillaId, hoja: hojaONull(hoja), rango: r },
    pasos: [{ tipo: "whatsapp", a: ["yo"], texto: TEXTO_AVISO }],
  };
}

/* ¿Esta hoja (por nombre e índice) es la que mira el disparador? `hoja: null`
   es la primera, y los nombres se comparan sin mayúsculas, igual que las
   referencias entre hojas del motor. */
export function esLaHoja(hojaDisparador, nombreHoja, indiceHoja) {
  if (hojaDisparador == null || txt(hojaDisparador) === "") return indiceHoja === 0;
  return txt(hojaDisparador).toLowerCase() === txt(nombreHoja).toLowerCase();
}

/* Los rangos que vigilan las automatizaciones dadas sobre UNA hoja de UNA
   planilla: `[{ id, nombre, rango, rg }]`. Lo usa la campanita de las celdas. */
export function rangosVigilados(autos, planillaId, nombreHoja, indiceHoja) {
  return (autos || [])
    .filter((a) => a && a.activa && a.disparador && a.disparador.tipo === "planilla_cambia"
      && a.disparador.planilla_id === planillaId && esLaHoja(a.disparador.hoja, nombreHoja, indiceHoja))
    .map((a) => ({ id: a.id, nombre: a.nombre, rango: a.disparador.rango, rg: parseRango(a.disparador.rango) }))
    .filter((x) => x.rg);
}

export const celdaEnRangos = (rangos, r, c) =>
  (rangos || []).some(({ rg }) => r >= rg.r0 && r <= rg.r1 && c >= rg.c0 && c <= rg.c1);

/* ── Validar antes de guardar ──────────────────────────────────────────
 *
 * Devuelve `[{ donde, mensaje }]`; vacío = se puede guardar. `donde` es
 * "nombre", "disparador", "pasos" o "paso:<_k>", para que la pantalla pinte
 * el error en su bloque. `ctx` trae lo que el formulario solo no sabe:
 *   planillasMias  ids de las planillas del dueño (para escribir y mirar)
 *   planillas      ids de todas las que puede ver (para leer)
 *   hojas          { [planilla_id]: ["Hoja 1", …] } las que ya se conocen
 *   gente          mails de `gente_para_compartir()`
 * Lo que no viene en `ctx` no se controla (mejor no frenar por un dato que
 * no se pudo traer: el bot lo vuelve a mirar al correr). */
export function validar(form, ctx = {}) {
  const errores = [];
  const err = (donde, mensaje) => errores.push({ donde, mensaje });
  const f = form || {};
  const d = f.disparador || {};
  const pasos = ordenarPasos(f.pasos || []);
  const mias = ctx.planillasMias ? new Set(ctx.planillasMias) : null;
  const visibles = ctx.planillas ? new Set(ctx.planillas) : null;
  const gente = ctx.gente ? new Set(ctx.gente.map((m) => String(m).toLowerCase())) : null;

  const hojaOk = (donde, planillaId, hoja) => {
    const hs = ctx.hojas && ctx.hojas[planillaId];
    if (!hs || hoja == null || txt(hoja) === "") return;
    if (!hs.some((h) => h.toLowerCase() === txt(hoja).toLowerCase())) err(donde, `La planilla no tiene una hoja «${hoja}».`);
  };
  const variablesOk = (donde, textos, disponibles) => {
    textos.forEach((t) => variablesUsadas(t).forEach((v) => {
      if (!disponibles.includes(v)) {
        err(donde, disponibles.length
          ? `{{${v}}} no existe acá. Las que hay: ${disponibles.map((x) => `{{${x}}}`).join(" ")}.`
          : `{{${v}}} no existe acá: este bloque no tiene variables para usar.`);
      }
    }));
  };

  if (!txt(f.nombre)) err("nombre", "Ponele un nombre, para reconocerla en la lista.");

  if (d.tipo === "horario") {
    if (!(d.dias || []).length) err("disparador", "Elegí al menos un día.");
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(txt(d.hora))) err("disparador", "La hora tiene que ser HH:MM (por ejemplo 09:00).");
  } else if (d.tipo === "mail") {
    // Los dos campos son opcionales: sin nada, corre con cada mail de la etiqueta.
  } else if (d.tipo === "planilla_cambia") {
    if (!txt(d.planilla_id)) err("disparador", "Elegí cuál de tus planillas mirar.");
    else if (mias && !mias.has(d.planilla_id)) err("disparador", "Sólo se puede mirar una planilla tuya.");
    else hojaOk("disparador", d.planilla_id, d.hoja);
    if (!txt(d.rango)) err("disparador", "Escribí qué rango mirar, por ejemplo B2:D10.");
    else if (!rangoValido(d.rango)) err("disparador", `«${txt(d.rango)}» no es un rango: tiene que ser como B2:D10 o una celda como B3.`);
  } else {
    err("disparador", "Elegí cuándo corre.");
  }

  if (!pasos.some((p) => grupoDe(p.tipo) === "hace")) err("pasos", "Falta al menos un bloque «Hacé…»: si no, no hace nada.");

  const nombresTrigger = VARIABLES_DISPARADOR[d.tipo] || [];
  pasos.forEach((p, i) => {
    const donde = `paso:${p._k ?? i}`;
    const disponibles = variablesDisponibles(d, pasos, i);
    if (!PASOS[p.tipo]) { err(donde, "Este bloque no existe."); return; }

    if (PASOS[p.tipo].guarda) {
      const n = txt(p.guardar_como);
      if (!n) err(donde, "Falta el nombre con que se guarda el dato («guardar como»).");
      else if (!nombreVariableValido(n)) err(donde, `«${n}» no sirve de nombre: letras, números y _ , sin espacios.`);
      else if (nombresTrigger.includes(n)) err(donde, `«${n}» ya lo usa el disparador: elegí otro nombre.`);
      else if (disponibles.includes(n)) err(donde, `«${n}» ya lo guardó un bloque anterior: elegí otro nombre.`);
    }

    switch (p.tipo) {
      case "leer_mail_ia":
        if (d.tipo !== "mail") err(donde, "«Leer el mail con IA» sólo tiene sentido cuando el disparador es un mail.");
        if (!txt(p.que)) err(donde, "Contá qué dato sacar del mail.");
        break;
      case "leer_celda":
        if (!txt(p.planilla_id)) err(donde, "Elegí de qué planilla leer.");
        else if (visibles && !visibles.has(p.planilla_id)) err(donde, "Esa planilla no está entre las que podés ver.");
        else hojaOk(donde, p.planilla_id, p.hoja);
        if (!celdaValida(p.celda)) err(donde, txt(p.celda) ? `«${txt(p.celda)}» no es una celda (por ejemplo B3).` : "Falta la celda, por ejemplo B3.");
        break;
      case "escribir_celda":
        if (!txt(p.planilla_id)) err(donde, "Elegí en cuál de tus planillas escribir.");
        else if (mias && !mias.has(p.planilla_id)) err(donde, "Sólo se puede escribir en una planilla tuya.");
        else hojaOk(donde, p.planilla_id, p.hoja);
        if (!celdaValida(p.celda)) err(donde, txt(p.celda) ? `«${txt(p.celda)}» no es una celda (por ejemplo B3).` : "Falta la celda, por ejemplo B3.");
        if (!txt(p.valor)) err(donde, "Falta qué escribir.");
        variablesOk(donde, [p.valor], disponibles);
        break;
      case "whatsapp":
      case "mail": {
        const a = destinatarios(p.a);
        if (!a.length) err(donde, "Elegí a quién mandarlo.");
        a.filter((m) => m !== "yo" && gente && !gente.has(m)).forEach((m) => err(donde, `${m} no es alguien del hub: sólo se manda a gente de adentro.`));
        if (p.tipo === "mail" && !txt(p.asunto)) err(donde, "Falta el asunto del mail.");
        if (!txt(p.texto)) err(donde, "Falta el texto.");
        variablesOk(donde, p.tipo === "mail" ? [p.asunto, p.texto] : [p.texto], disponibles);
        break;
      }
      case "mandar_planilla": {
        const a = destinatarios(p.a);
        if (!txt(p.planilla_id)) err(donde, "Elegí qué planilla mandar.");
        else if (visibles && !visibles.has(p.planilla_id)) err(donde, "Esa planilla no está entre las que podés ver.");
        else if (mias && !mias.has(p.planilla_id) && a.some((m) => m !== "yo")) {
          err(donde, "Esa planilla te la compartieron: se te puede mandar sólo a vos, no a otros.");
        }
        if (!canales(p.por).length) err(donde, "Elegí cómo mandarla: por mail, por WhatsApp o las dos.");
        if (!a.length) err(donde, "Elegí a quién mandarla.");
        a.filter((m) => m !== "yo" && gente && !gente.has(m)).forEach((m) => err(donde, `${m} no es alguien del hub: sólo se manda a gente de adentro.`));
        break;
      }
      default:
    }
  });
  return errores;
}

/* ── Textos para mostrar ───────────────────────────────────────────────── */

/* «Lun, Mié y Vie» · «De lunes a viernes» · «Todos los días» · «Los viernes». */
export function textoDias(dias) {
  const ds = [...new Set((dias || []).map(Number))].filter((n) => n >= 0 && n <= 6).sort((a, b) => a - b);
  if (!ds.length) return "Ningún día";
  if (ds.length === 7) return "Todos los días";
  if (ds.join() === "1,2,3,4,5") return "De lunes a viernes";
  if (ds.join() === "0,6") return "Los fines de semana";
  if (ds.length === 1) return `Los ${DIAS_LARGOS[ds[0]]}`;
  const nombres = ds.map((n) => DIAS[n]);
  return `${nombres.slice(0, -1).join(", ")} y ${nombres[nombres.length - 1]}`;
}

/* El disparador en un renglón, para la tarjeta de la lista. `planillas` es
   `{ [id]: nombre }`; si no se sabe el nombre, se dice «una planilla». */
export function resumenDisparador(d, planillas = {}) {
  const x = d || {};
  if (x.tipo === "horario") return `${textoDias(x.dias)} a las ${txt(x.hora) || "?"}`;
  if (x.tipo === "mail") {
    const de = txt(x.remitente);
    const as = txt(x.asunto);
    if (!de && !as) return "Cuando llega cualquier mail a Automatizaciones";
    return ["Cuando llega un mail", de && `de «${de}»`, as && `con «${as}» en el asunto`].filter(Boolean).join(" ");
  }
  if (x.tipo === "planilla_cambia") {
    const nombre = planillas[x.planilla_id];
    const donde = nombre ? `«${nombre}»` : "una planilla";
    return `Cuando cambia ${txt(x.rango) || "?"} de ${donde}${txt(x.hoja) ? ` (${txt(x.hoja)})` : ""}`;
  }
  return "Sin disparador";
}

const quienes = (a, gente = {}) => destinatarios(a).map((m) => (m === "yo" ? "vos" : (gente[m] || m))).join(", ");

/* Un paso en un renglón: «WhatsApp a vos», «Escribir B3», «Leer B3 → {{valor}}». */
export function resumenPaso(p, gente = {}) {
  const x = p || {};
  switch (x.tipo) {
    case "leer_mail_ia": return `Leer del mail «${txt(x.que)}» → {{${txt(x.guardar_como)}}}`;
    case "leer_celda": return `Leer ${txt(x.celda)} → {{${txt(x.guardar_como)}}}`;
    case "escribir_celda": return `Escribir en ${txt(x.celda)}`;
    case "whatsapp": return `WhatsApp a ${quienes(x.a, gente) || "nadie"}`;
    case "mail": return `Mail a ${quienes(x.a, gente) || "nadie"}`;
    case "mandar_planilla": {
      const por = canales(x.por).map((c) => (c === "mail" ? "mail" : "WhatsApp")).join(" y ");
      return `Mandar una planilla${por ? ` por ${por}` : ""} a ${quienes(x.a, gente) || "nadie"}`;
    }
    default: return "Bloque desconocido";
  }
}

export const ESTADOS_CORRIDA = {
  pedida: { label: "Pedida", tono: "info" },
  corriendo: { label: "Corriendo", tono: "info" },
  ok: { label: "OK", tono: "ok" },
  error: { label: "Error", tono: "error" },
  sin_cambios: { label: "Sin cambios", tono: "neutro" },
};
export const TERMINADAS = ["ok", "error", "sin_cambios"];
export const estadoCorrida = (e) => ESTADOS_CORRIDA[e] || { label: e || "?", tono: "neutro" };

/* La última corrida de cada automatización, de una lista ordenada o no. */
export function ultimaPorAutomatizacion(corridas) {
  const salida = {};
  (corridas || []).forEach((c) => {
    const prev = salida[c.automatizacion_id];
    if (!prev || String(c.pedida_en) > String(prev.pedida_en)) salida[c.automatizacion_id] = c;
  });
  return salida;
}

/* ── El `detalle` de una corrida, legible ────────────────────────────────
 *
 * La forma del `detalle` la decide el bot y el contrato no la fija. Así que se
 * lee lo que se reconoce y lo demás se muestra igual, prolijo, en vez de
 * esconderlo: un dato que el bot dejó y la pantalla calla es un dato perdido
 * justo cuando alguien está mirando por qué algo no anduvo.
 *
 * Lo que se reconoce (sugerido al bot, no obligatorio):
 *   variables / leido   { nombre: valor }  → «Leyó»
 *   acciones / pasos    [{ tipo, a, texto, asunto, celda, valor, … }]
 *                                          → «Haría» (prueba) / «Hizo» (real)
 *   cambios             texto o lista      → «Cambios»
 *   mensaje / nota      texto
 * Devuelve `[{ titulo, lineas: [texto] }]`.
 *
 * ── LO QUE EL BOT ANOTA PARA ÉL NO SE DIBUJA ────────────────────────────
 *
 * «Lo demás se muestra igual» vale para lo que NO se conoce. Estas tres sí se
 * conocen, y son la bitácora del bot (`automatizaciones.py`, `ejecutar`):
 * `modo` repite lo que ya dice el título, y `disparador` y `pasos` son el
 * registro paso a paso, con el id del mail, los teléfonos y con qué se leyó.
 * Caían en «Además» como JSON crudo, a la vista del que probaba.
 * `pasos` se calla sólo cuando vino `acciones`: si es lo único que hay, es la
 * lista de lo que hizo (ver arriba). */
const DEL_BOT = ["modo", "disparador", "pasos"];
const corto = (v) => {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try { return JSON.stringify(v); } catch { return String(v); }
};

function lineaAccion(a, gente) {
  if (!a || typeof a !== "object") return corto(a);
  const extra = [];
  if (a.tipo === "whatsapp" || a.tipo === "mail") {
    const destino = quienes(a.a || a.destinatarios, gente);
    const cab = `${a.tipo === "mail" ? "Mail" : "WhatsApp"}${destino ? ` a ${destino}` : ""}`;
    if (a.asunto) extra.push(`«${a.asunto}»`);
    if (a.texto != null) extra.push(String(a.texto));
    return [cab, ...extra].join(": ");
  }
  if (a.tipo === "mandar_planilla") {
    const destino = quienes(a.a, gente);
    const cab = a.por === "whatsapp"
      ? `Resumen de «${a.planilla || "?"}» por WhatsApp`
      : `Planilla «${a.planilla || "?"}» por mail (Excel)`;
    return `${cab}${destino ? ` a ${destino}` : ""}`;
  }
  if (a.tipo === "escribir_celda") {
    return `Escribir ${corto(a.valor)} en ${a.celda || "?"}${a.hoja ? ` (${a.hoja})` : ""}${a.planilla ? ` de «${a.planilla}»` : ""}`;
  }
  if (a.tipo === "leer_celda" || a.tipo === "leer_mail_ia") {
    return `${a.tipo === "leer_celda" ? `Leer ${a.celda || "?"}` : "Leer el mail"}${a.guardar_como ? ` → ${a.guardar_como}` : ""}${a.valor !== undefined ? ` = ${corto(a.valor)}` : ""}`;
  }
  return Object.entries(a).map(([k, v]) => `${k}: ${corto(v)}`).join(" · ");
}

export function detalleLegible(detalle, { prueba = false, gente = {} } = {}) {
  if (detalle == null || detalle === "") return [];
  if (typeof detalle !== "object") return [{ titulo: "", lineas: [corto(detalle)] }];
  if (Array.isArray(detalle)) return [{ titulo: "", lineas: detalle.map((a) => lineaAccion(a, gente)) }];
  const secciones = [];
  const usadas = new Set();
  const vars = detalle.variables || detalle.leido;
  if (vars && typeof vars === "object" && !Array.isArray(vars)) {
    usadas.add(detalle.variables ? "variables" : "leido");
    secciones.push({ titulo: "Leyó", lineas: Object.entries(vars).map(([k, v]) => `{{${k}}} = ${corto(v)}`) });
  }
  if (detalle.cambios != null) {
    usadas.add("cambios");
    const ls = Array.isArray(detalle.cambios) ? detalle.cambios.map(corto) : String(detalle.cambios).split("\n");
    secciones.push({ titulo: "Cambios", lineas: ls.filter((l) => l !== "") });
  }
  const acciones = detalle.acciones || detalle.pasos;
  if (Array.isArray(acciones)) {
    usadas.add(detalle.acciones ? "acciones" : "pasos");
    secciones.push({ titulo: prueba ? "Habría hecho (es una prueba: no escribió ni mandó nada)" : "Hizo", lineas: acciones.map((a) => lineaAccion(a, gente)) });
  }
  /* El `mensaje` del bot es el resumen de UNA línea de esas mismas acciones
     («Prueba: mandaría un WhatsApp a nico@…»). Con las acciones ya listadas
     arriba es la misma cosa dos veces, y la segunda peor: con el mail en vez
     del nombre. Queda cuando no hubo acciones, que ahí es lo único que dice
     qué pasó («nada cambió», «no hay mail»). */
  const yaDicho = Array.isArray(acciones) && acciones.length > 0;
  ["mensaje", "nota"].forEach((k) => {
    if (detalle[k] == null) return;
    usadas.add(k);
    if (k === "mensaje" && yaDicho) return;
    secciones.push({ titulo: "", lineas: [corto(detalle[k])] });
  });
  DEL_BOT.forEach((k) => usadas.add(k));
  const resto = Object.entries(detalle).filter(([k]) => !usadas.has(k));
  if (resto.length) secciones.push({ titulo: secciones.length ? "Además" : "", lineas: resto.map(([k, v]) => `${k}: ${corto(v)}`) });
  return secciones;
}

/* Un renglón del detalle, para la tabla del historial. */
export function resumenDetalle(detalle, opciones) {
  const secciones = detalleLegible(detalle, opciones);
  const primera = secciones.flatMap((s) => s.lineas)[0] || "";
  return primera.length > 120 ? `${primera.slice(0, 117)}…` : primera;
}

/* `[{ mail, nombre }]` → `{ mail: nombre }`, en minúsculas. */
export function mapaDeGente(gente) {
  const m = {};
  (gente || []).forEach((g) => { if (g && g.mail) m[String(g.mail).toLowerCase()] = g.nombre || g.mail; });
  return m;
}
